import apiClient from './apiClient';
import type {
  RockTCSConformityReading,
  RockTCSData,
  RockTCSGeneral,
  RockTCSReadingsPayload,
  RockTCSUltrasonicReading,
  RockTCSUploadResult,
} from '../types/rockTcs';

export const getRockTcsData = async (workOrderDetailKey: string): Promise<RockTCSData> => {
  const r = await apiClient.get<RockTCSData>(`/workorderdetails/${workOrderDetailKey}/rocktcs`);
  return r.data;
};

export const saveRockTcsData = async (
  workOrderDetailKey: string,
  general: RockTCSGeneral,
  conformityReadings: RockTCSConformityReading[],
  ultrasonicReadings: RockTCSUltrasonicReading[],
  confirmReset?: boolean,
): Promise<RockTCSData> => {
  const r = await apiClient.put(`/workorderdetails/${workOrderDetailKey}/rocktcs`, {
    general,
    conformityReadings,
    ultrasonicReadings,
    confirmReset,
  });
  return r.data;
};

export const getRockTcsReadings = async (workOrderDetailKey: string): Promise<RockTCSReadingsPayload> => {
  const r = await apiClient.get<RockTCSReadingsPayload>(`/workorderdetails/${workOrderDetailKey}/rocktcs/readings`);
  return r.data;
};

/** One reading row posted by the editable Test Data grid (raw inputs + machine passthrough). */
export interface RockTCSReadingEdit {
  timeS: number | null;
  frameLoadKN: number | null;
  axialDisp1Mm: number | null;
  axialDisp2Mm: number | null;
  radialStrainPct: number | null;
  machineDeviatorStressMPa: number | null;
  machineConfiningPressureMPa: number | null;
  machinePorePressureMPa: number | null;
  machineAxialStrainPct: number | null;
  machineVolStrainPct: number | null;
}

/**
 * Save a hand-edited readings set (add / edit / delete rows). The backend
 * recomputes every derived column + the peak/summary, then returns the fresh
 * payload. A 409 `TEST_REQUIRES_RESET` is surfaced for protected statuses —
 * retry with `confirmReset = true`.
 */
export const saveRockTcsReadings = async (
  workOrderDetailKey: string,
  rows: RockTCSReadingEdit[],
  confirmReset?: boolean,
): Promise<RockTCSReadingsPayload & { resetPerformed?: boolean }> => {
  const r = await apiClient.put<RockTCSReadingsPayload & { resetPerformed?: boolean }>(
    `/workorderdetails/${workOrderDetailKey}/rocktcs/readings`,
    { rows, confirmReset },
  );
  return r.data;
};

/** Upload a GCTS CATS CSV — sent as text in the JSON body (≈100 KB files). */
export const uploadRockTcsCsv = async (
  workOrderDetailKey: string,
  file: File,
): Promise<RockTCSUploadResult> => {
  const content = await file.text();
  const r = await apiClient.post<RockTCSUploadResult>(
    `/workorderdetails/${workOrderDetailKey}/rocktcs/upload`,
    { fileName: file.name, content },
  );
  return r.data;
};
